// ── Hero Background Canvas — Dark fog + warm gold light rays ──
export class HeroBgCanvas {
    constructor(canvasId = 'hero-bg-canvas') {
        this.canvas = document.getElementById(canvasId);
        if (!this.canvas) return;
        this.ctx = this.canvas.getContext('2d');
        this.width = 0;
        this.height = 0;
        this.t = 0;
        this.rafId = null;
        this.particles = [];
        this.rays = [];

        this.resize();
        this.initParticles();
        this.initRays();
        window.addEventListener('resize', () => this.resize());
        this.play();
    }

    resize() {
        if (!this.canvas) return;
        this.width = this.canvas.width = this.canvas.offsetWidth;
        this.height = this.canvas.height = this.canvas.offsetHeight;
    }

    initParticles() {
        this.particles = [];
        const count = Math.min(60, Math.floor((this.width * this.height) / 14000));
        for (let i = 0; i < count; i++) {
            this.particles.push(this.makeParticle());
        }
    }

    makeParticle(fromBottom = false) {
        return {
            x: Math.random() * this.width,
            y: fromBottom ? this.height + 10 : Math.random() * this.height,
            size: 0.5 + Math.random() * 1.5,
            speed: 0.08 + Math.random() * 0.18,
            opacity: 0,
            maxOpacity: 0.15 + Math.random() * 0.35,
            fade: 0.002 + Math.random() * 0.003,
            drift: (Math.random() - 0.5) * 0.15,
            phase: 'in', // 'in' | 'hold' | 'out'
            holdFrames: 60 + Math.floor(Math.random() * 120),
            hf: 0,
        };
    }

    initRays() {
        this.rays = [];
        const count = 5 + Math.floor(Math.random() * 3);
        for (let i = 0; i < count; i++) {
            this.rays.push(this.makeRay());
        }
    }

    makeRay(forced = false) {
        const cx = this.width * (0.3 + Math.random() * 0.4);
        const cy = this.height * (0.6 + Math.random() * 0.3);
        const angleSpread = (Math.PI / 5) + Math.random() * (Math.PI / 4);
        const baseAngle = -Math.PI / 2 + (Math.random() - 0.5) * 0.6;
        return {
            cx, cy,
            angle: baseAngle,
            spread: angleSpread,
            length: this.height * (0.5 + Math.random() * 0.6),
            opacity: 0,
            maxOpacity: 0.04 + Math.random() * 0.06,
            fadeSpeed: 0.0008 + Math.random() * 0.001,
            phase: forced ? 'in' : (Math.random() < 0.5 ? 'in' : 'hold'),
            hf: 0,
            holdFrames: 200 + Math.floor(Math.random() * 300),
            color1: Math.random() < 0.7 ? '212,175,55' : '255,200,100',
        };
    }

    drawRay(r) {
        const { ctx } = this;
        const a1 = r.angle - r.spread / 2;
        const a2 = r.angle + r.spread / 2;
        const x1 = r.cx + Math.cos(a1) * r.length;
        const y1 = r.cy + Math.sin(a1) * r.length;
        const x2 = r.cx + Math.cos(a2) * r.length;
        const y2 = r.cy + Math.sin(a2) * r.length;

        const grad = ctx.createRadialGradient(r.cx, r.cy, 0, r.cx, r.cy, r.length);
        grad.addColorStop(0, `rgba(${r.color1},${r.opacity})`);
        grad.addColorStop(0.4, `rgba(${r.color1},${r.opacity * 0.4})`);
        grad.addColorStop(1, `rgba(${r.color1},0)`);

        ctx.beginPath();
        ctx.moveTo(r.cx, r.cy);
        ctx.lineTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.closePath();
        ctx.fillStyle = grad;
        ctx.fill();
    }

    updateRay(r) {
        if (r.phase === 'in') {
            r.opacity = Math.min(r.maxOpacity, r.opacity + r.fadeSpeed);
            if (r.opacity >= r.maxOpacity) { r.phase = 'hold'; r.hf = 0; }
        } else if (r.phase === 'hold') {
            r.hf++;
            if (r.hf >= r.holdFrames) r.phase = 'out';
        } else {
            r.opacity = Math.max(0, r.opacity - r.fadeSpeed);
            if (r.opacity <= 0) Object.assign(r, this.makeRay(true));
        }
    }

    drawFog() {
        const { ctx, width, height, t } = this;
        // Slow drifting fog wisps
        for (let i = 0; i < 3; i++) {
            const ox = Math.sin(t * 0.0004 + i * 2.1) * width * 0.15;
            const oy = Math.cos(t * 0.0003 + i * 1.7) * height * 0.08;
            const cx = width * (0.2 + i * 0.3) + ox;
            const cy = height * (0.4 + i * 0.15) + oy;
            const r = width * (0.3 + i * 0.1);
            const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
            grad.addColorStop(0, `rgba(30,20,10,0.18)`);
            grad.addColorStop(0.5, `rgba(15,10,5,0.08)`);
            grad.addColorStop(1, `rgba(0,0,0,0)`);
            ctx.beginPath();
            ctx.ellipse(cx, cy, r, r * 0.5, 0, 0, Math.PI * 2);
            ctx.fillStyle = grad;
            ctx.fill();
        }
    }

    draw() {
        const { ctx, width, height, t } = this;

        // Clear
        ctx.fillStyle = '#080604';
        ctx.fillRect(0, 0, width, height);

        // Subtle vignette base
        const vig = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, Math.max(width, height) * 0.7);
        vig.addColorStop(0, 'rgba(15,10,5,0)');
        vig.addColorStop(1, 'rgba(0,0,0,0.7)');
        ctx.fillStyle = vig;
        ctx.fillRect(0, 0, width, height);

        // Fog wisps
        this.drawFog();

        // Light rays
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        this.rays.forEach(r => { this.updateRay(r); this.drawRay(r); });
        ctx.restore();

        // Floating dust particles
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        this.particles.forEach(p => {
            // Update
            p.y -= p.speed;
            p.x += p.drift;
            if (p.phase === 'in') {
                p.opacity = Math.min(p.maxOpacity, p.opacity + p.fade);
                if (p.opacity >= p.maxOpacity) { p.phase = 'hold'; p.hf = 0; }
            } else if (p.phase === 'hold') {
                p.hf++;
                if (p.hf >= p.holdFrames) p.phase = 'out';
            } else {
                p.opacity = Math.max(0, p.opacity - p.fade);
                if (p.opacity <= 0) Object.assign(p, this.makeParticle(true));
            }

            // Draw
            const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 3);
            g.addColorStop(0, `rgba(212,175,55,${p.opacity})`);
            g.addColorStop(1, 'rgba(212,175,55,0)');
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 3, 0, Math.PI * 2);
            ctx.fillStyle = g;
            ctx.fill();
        });
        ctx.restore();

        // Bottom warm glow (floor-level warm light)
        const floorGlow = ctx.createLinearGradient(0, height * 0.75, 0, height);
        floorGlow.addColorStop(0, 'rgba(180,100,20,0)');
        floorGlow.addColorStop(1, 'rgba(180,100,20,0.07)');
        ctx.fillStyle = floorGlow;
        ctx.fillRect(0, 0, width, height);
    }

    play() {
        const loop = (ts) => {
            this.t = ts;
            this.draw();
            this.rafId = requestAnimationFrame(loop);
        };
        this.rafId = requestAnimationFrame(loop);
    }

    destroy() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
    }
}
